import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import MobileStepper from "@mui/material/MobileStepper";
import Typography from "@mui/material/Typography";

/**
 * Tour de boas-vindas do Background IA.
 *
 * Fechar ou "Pular" suprime o tour até a aba do navegador ser encerrada.
 * "Não mostrar novamente" grava também a preferência permanente. Reabrir uma
 * rota na mesma sessão não pode bloquear o professor com o mesmo modal.
 */
export const CHAVE_WIZARD_ENTREVISTA = "hipd_interview_wizard_v1";
export const CHAVE_WIZARD_SESSAO = "hipd_interview_wizard_session_v1";

export function wizardJaDispensado(): boolean {
  try {
    if (window.localStorage.getItem(CHAVE_WIZARD_ENTREVISTA) === "1") {
      return true;
    }
  } catch {
    // A preferência permanente é opcional; ainda tente a sessão.
  }

  try {
    return window.sessionStorage.getItem(CHAVE_WIZARD_SESSAO) === "1";
  } catch {
    // Sem storage, mostrar ajuda outra vez é menos destrutivo que ocultá-la.
    return false;
  }
}

const PASSOS = [
  "interview_wizard_step1",
  "interview_wizard_step2",
  "interview_wizard_step3",
  "interview_wizard_step4",
] as const;

export default function WizardEntrevista({
  aberto,
  aoFechar,
}: {
  aberto: boolean;
  aoFechar: () => void;
}) {
  const { t } = useTranslation();
  const [passo, setPasso] = useState(0);
  const [naoMostrarMais, setNaoMostrarMais] = useState(false);

  const ultimo = passo === PASSOS.length - 1;

  const fechar = () => {
    try {
      window.sessionStorage.setItem(CHAVE_WIZARD_SESSAO, "1");
    } catch {
      /* sem storage, a tela continua funcional */
    }

    if (naoMostrarMais) {
      try {
        window.localStorage.setItem(CHAVE_WIZARD_ENTREVISTA, "1");
      } catch {
        /* sem storage, sem dispensa permanente */
      }
    }
    aoFechar();
  };

  return (
    <Dialog
      open={aberto}
      onClose={fechar}
      maxWidth="sm"
      fullWidth
      aria-label={t("interview_wizard_title") || undefined}
    >
      <DialogTitle sx={{ pb: 1 }}>{t("interview_wizard_title")}</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {t(`${PASSOS[passo]}_title`)}
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
          {t(`${PASSOS[passo]}_body`)}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ flexDirection: "column", alignItems: "stretch", px: 3, pb: 2 }}>
        <MobileStepper
          variant="dots"
          steps={PASSOS.length}
          position="static"
          activeStep={passo}
          sx={{ justifyContent: "center", bgcolor: "transparent", pb: 1 }}
          backButton={<span />}
          nextButton={<span />}
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={naoMostrarMais}
                onChange={(e) => setNaoMostrarMais(e.target.checked)}
              />
            }
            label={
              <Typography variant="caption">
                {t("interview_wizard_never_show")}
              </Typography>
            }
            sx={{ mr: "auto" }}
          />
          <Button onClick={fechar} color="inherit" size="small">
            {t("interview_wizard_skip")}
          </Button>
          {passo > 0 && (
            <Button onClick={() => setPasso(passo - 1)} size="small">
              {t("interview_wizard_back")}
            </Button>
          )}
          <Button
            variant="contained"
            size="small"
            onClick={() => (ultimo ? fechar() : setPasso(passo + 1))}
          >
            {ultimo ? t("interview_wizard_start") : t("interview_wizard_next")}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
